import type { Control, FieldValues, Path } from "react-hook-form";
import { useFormState } from "react-hook-form";
import { FormCheckboxField } from "@/components/base/form/form-checkbox-field";
import { FormField, FormControl, FormItem, FormLabel } from "@/components/base/form/hook-form";
import { Input } from "@/components/base/input/input";
import { z } from "zod";

export interface ParticipationLimitFieldNames {
	hasParticipationLimit: string;
	maxClaimsPerRoster: string;
}

export interface ParticipationLimitSectionProps<T extends FieldValues> {
	control: Control<T>;
	fieldNames: ParticipationLimitFieldNames;
	hasParticipationLimit: boolean;
	onToggle?: () => void;
}

export const ParticipationLimitSection = <T extends FieldValues>({
	control,
	fieldNames,
	hasParticipationLimit,
	onToggle,
}: ParticipationLimitSectionProps<T>) => {
	const { errors } = useFormState({ control });
	const fieldError = errors[fieldNames.maxClaimsPerRoster as keyof typeof errors];
	const isInvalid = !!fieldError;

	return (
		<div>
			<FormField
				control={control}
				name={fieldNames.hasParticipationLimit as Path<T>}
				render={({ field }) => (
					<FormCheckboxField
						field={field}
						label="Set Participation Limit"
						inputClasses="w-fit"
						onChange={() => onToggle?.()}
					/>
				)}
			/>
			<div className="ml-8">
				<p className="text-quaternary mb-3">
					Limits the number of participants that can claim an Opp
				</p>
				{hasParticipationLimit && (
					<FormField
						control={control}
						name={fieldNames.maxClaimsPerRoster as Path<T>}
						render={({ field }) => (
							<FormItem>
								<FormLabel>Limit</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="number"
										className="w-fit"
										isInvalid={isInvalid}
										aria-label="Participation limit"
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				)}
			</div>
		</div>
	);
};

export const participationLimitSchemaFields = {
	hasParticipationLimit: z.boolean(),
	maxClaimsPerRoster: z.union([z.number(), z.string()]).optional(),
};

export const participationLimitRefinement = (values: { hasParticipationLimit: boolean; maxClaimsPerRoster?: number | string }) => {
	if (!values.hasParticipationLimit) return true;
	if (values.maxClaimsPerRoster === undefined || values.maxClaimsPerRoster === "") return false;
	return Number(values.maxClaimsPerRoster) >= 1;
};

export const participationLimitRefinementMessage = "Participation limit must be at least 1";
